import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiGlobe } from 'react-icons/fi';
import { SELECTABLE_LANGUAGES } from '@/i18n/languages';
import { useLocaleChange } from '@/hooks/useLocaleChange';

export default function LanguageSwitcher() {
  const { t } = useTranslation();
  const { currentLocale, changeLocale } = useLocaleChange();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = SELECTABLE_LANGUAGES.find((lang) => lang.code === currentLocale) ?? SELECTABLE_LANGUAGES[0];

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (SELECTABLE_LANGUAGES.length <= 1) {
    return null;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={t('header.selectLanguage')}
        aria-label={t('header.selectLanguage')}
        aria-expanded={open}
        className="flex h-9 min-w-9 items-center justify-center gap-1 rounded-[var(--radius-sm)] px-1.5 text-text-secondary transition-colors hover:bg-surface-hover"
      >
        <FiGlobe className="h-[17px] w-[17px] shrink-0" aria-hidden />
        <span className="hidden text-[10px] font-bold uppercase tracking-wide sm:inline">{active.code}</span>
      </button>

      {open && (
        <div className="header-popup absolute end-0 z-[100] mt-2 w-52 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
          <p className="border-b border-border px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-text-primary">
            {t('header.language')}
          </p>
          <ul className="max-h-72 overflow-y-auto py-1 scrollbar-thin" role="listbox">
            {SELECTABLE_LANGUAGES.map((lang) => {
              const selected = lang.code === currentLocale;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      void changeLocale(lang.code);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-start text-[12px] transition-colors ${
                      selected ? 'bg-brand/10 font-semibold text-brand' : 'text-text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    <span>{lang.nativeLabel}</span>
                    <span className="text-[10px] uppercase text-text-muted">{lang.code}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
